import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ClaimHeader from './components/ClaimHeader';
import EvidenceSection from './components/EvidenceSection';
import VerdictTimeline from './components/VerdictTimeline';
import RelatedClaimsPanel from './components/RelatedClaimsPanel';
import ShareExportModal from './components/ShareExportModal';

const ClaimDetailAnalysis = () => {
  const { claimId } = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeView, setActiveView] = useState('overview');

  // Mock data for the claim analysis
  const claimData = {
    id: "claim_2025_001847",
    statement: "The European Union has secretly allocated €50 billion to fund climate change denial campaigns across member states to undermine the Green Deal implementation.",
    verdict: "False",
    confidenceScore: 92,
    efcsnBadge: true,
    lastUpdated: "2 hours ago",
    reviewerCount: 8,
    countries: ["Germany", "France", "Spain", "Italy"],
    category: "Climate Policy",
    viewCount: 15847,
    createdDate: "2025-01-15",
    originalSource: "Social media post with 2.3M views",
    riskLevel: "High",
    viralityScore: 87
  };

  const evidenceData = {
    sources: [
      {
        type: "government",
        title: "EU Budget 2024-2027 Official Documentation",
        publisher: "European Commission",
        excerpt: "Complete transparency report showing all climate-related funding allocations, with no evidence of denial campaign funding.",
        reliability: 95,
        date: "2024-12-15",
        language: "English",
        country: "Belgium"
      },
      {
        type: "academic",
        title: "Analysis of EU Climate Policy Funding Mechanisms",
        publisher: "European Policy Institute",
        excerpt: "Comprehensive review of EU climate funding shows consistent support for Green Deal implementation across all member states.",
        reliability: 88,
        date: "2024-11-20",
        language: "English",
        country: "Netherlands"
      },
      {
        type: "news",
        title: "EU Climate Budget Breakdown: Where Every Euro Goes",
        publisher: "EuroNews",
        excerpt: "Detailed investigation into EU climate spending reveals transparent allocation process with public oversight mechanisms.",
        reliability: 82,
        date: "2025-01-10",
        language: "English",
        country: "France"
      }
    ],
    forensics: [
      {
        type: "Document Analysis",
        method: "ELA (Error Level Analysis)",
        result: "authentic",
        confidence: 94,
        description: "Analysis of shared budget documents shows no signs of digital manipulation or forgery.",
        preview: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=200&fit=crop"
      },
      {
        type: "Source Verification",
        method: "Cross-reference Analysis",
        result: "verified",
        confidence: 91,
        description: "All cited EU budget documents verified against official European Commission databases.",
        preview: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=200&fit=crop"
      }
    ],
    attachments: [
      {
        name: "EU_Budget_Climate_Allocation_2024.pdf",
        type: "document",
        size: "2.4 MB",
        format: "PDF",
        uploadDate: "2025-01-15",
        preview: "https://images.unsplash.com/photo-1568667256549-094345857637?w=400&h=200&fit=crop"
      },
      {
        name: "Green_Deal_Funding_Timeline.xlsx",
        type: "spreadsheet",
        size: "1.8 MB",
        format: "Excel",
        uploadDate: "2025-01-14"
      }
    ],
    c2pa: [
      {
        issuer: "European Commission Digital Authority",
        status: "verified",
        created: "2024-12-15T10:30:00Z",
        hash: "sha256:a1b2c3d4e5f6...",
        signature: "Valid RSA-2048"
      }
    ]
  };

  const timelineData = [
    {
      id: "timeline_001",
      type: "verdict_change",
      title: "Initial Assessment Completed",
      description: "First review by EFCSN fact-checkers established preliminary verdict based on available evidence.",
      timestamp: "2025-01-15 14:30",
      reviewer: "Dr. Maria Schmidt (EFCSN)",
      efcsnVerified: true,
      verdict: "Unverified",
      confidenceChange: { from: 45, to: 65 },
      hasDiff: true,
      changes: [
        { field: "Verdict", from: "Pending", to: "Unverified" },
        { field: "Evidence Count", from: "2", to: "5" }
      ],
      details: {
        methodology: "Applied European Code of Standards methodology with cross-reference verification of EU budget documents.",
        sources: [
          { title: "EU Budget Documentation", publisher: "European Commission" },
          { title: "Climate Policy Analysis", publisher: "European Policy Institute" }
        ]
      }
    },
    {
      id: "timeline_002",
      type: "evidence_added",
      title: "Additional Evidence Integrated",
      description: "Comprehensive forensics analysis and C2PA verification completed, strengthening the assessment.",
      timestamp: "2025-01-16 09:15",
      reviewer: "Prof. Jean Dubois (EFCSN)",
      efcsnVerified: true,
      confidenceChange: { from: 65, to: 85 },
      hasDiff: true,
      details: {
        sources: [
          { title: "ELA Forensics Report", publisher: "Digital Forensics Lab" },
          { title: "C2PA Verification Results", publisher: "EU Digital Authority" }
        ],
        peerReviews: [
          {
            reviewer: "Dr. Anna Kowalski",
            timestamp: "2025-01-16 11:20",
            comment: "Forensics analysis confirms document authenticity. Methodology is sound and follows EFCSN standards."
          }
        ]
      }
    },
    {
      id: "timeline_003",
      type: "peer_review",
      title: "Peer Review Consensus Reached",
      description: "Final verdict established after comprehensive peer review by 8 EFCSN experts across 4 member states.",
      timestamp: "2025-01-17 16:45",
      reviewer: "EFCSN Review Panel",
      efcsnVerified: true,
      verdict: "False",
      confidenceChange: { from: 85, to: 92 },
      hasDiff: true,
      changes: [
        { field: "Verdict", from: "Unverified", to: "False" },
        { field: "Confidence Score", from: "85%", to: "92%" }
      ],
      details: {
        methodology: "Multi-expert consensus review following two-person publishing rule with independent verification.",
        peerReviews: [
          {
            reviewer: "Dr. Hans Mueller",
            timestamp: "2025-01-17 14:30",
            comment: "Evidence clearly contradicts the claim. EU budget transparency mechanisms make such secret funding impossible."
          },
          {
            reviewer: "Dr. Sofia Rossi",
            timestamp: "2025-01-17 15:15",
            comment: "Comprehensive analysis confirms false nature of claim. Recommend immediate publication with high confidence."
          }
        ]
      }
    }
  ];

  const relatedClaimsData = [
    {
      id: "claim_2025_001823",
      statement: "EU secretly funds anti-renewable energy lobbying groups to slow down green transition",
      verdict: "False",
      similarity: 89,
      date: "2025-01-12",
      country: "Germany",
      language: "German",
      views: 8934,
      engagements: 1247,
      shares: 892
    },
    {
      id: "claim_2025_001801",
      statement: "European Commission allocates hidden budget for climate skeptic research",
      verdict: "Misleading",
      similarity: 76,
      date: "2025-01-08",
      country: "France",
      language: "French",
      views: 12456,
      engagements: 2134,
      shares: 1567
    },
    {
      id: "claim_2025_001789",
      statement: "EU Green Deal funding secretly diverted to fossil fuel companies",
      verdict: "False",
      similarity: 71,
      date: "2025-01-05",
      country: "Italy",
      language: "Italian",
      views: 6789,
      engagements: 934,
      shares: 567
    }
  ];

  const narrativeConnectionsData = [
    {
      id: "narrative_2025_climate_001",
      title: "EU Climate Policy Conspiracy Narrative",
      description: "Coordinated disinformation campaign suggesting EU climate policies are secretly undermined by internal funding of denial groups.",
      status: "active",
      timeframe: "Dec 2024 - Present",
      countries: ["Germany", "France", "Spain", "Italy", "Poland"],
      claimCount: 23,
      reach: 2400000,
      riskLevel: "High",
      evolution: [
        { intensity: "low", period: "Dec 2024" },
        { intensity: "medium", period: "Jan 2025" },
        { intensity: "high", period: "Present" }
      ]
    },
    {
      id: "narrative_2025_budget_002",
      title: "EU Budget Transparency Attacks",
      description: "Systematic attempts to undermine trust in EU financial transparency through false claims about secret funding allocations.",
      status: "active",
      timeframe: "Nov 2024 - Present",
      countries: ["Netherlands", "Belgium", "Austria"],
      claimCount: 15,
      reach: 1800000,
      riskLevel: "Medium",
      evolution: [
        { intensity: "medium", period: "Nov 2024" },
        { intensity: "high", period: "Dec 2024" },
        { intensity: "medium", period: "Present" }
      ]
    }
  ];

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleExport = () => {
    setShareModalOpen(true);
  };

  const handleRequestReview = () => {
    // Request peer review functionality
    console.log('Requesting peer review for claim:', claimData?.id);
  };

  const handleViewDiff = (entryId) => {
    // View diff functionality
    console.log('Viewing diff for timeline entry:', entryId);
  };

  const handleClaimClick = (claimId) => {
    // Navigate to related claim
    window.location.href = `/claim-detail-analysis/${claimId}`;
  };

  const handleNarrativeClick = (narrativeId) => {
    // Navigate to narrative explorer
    window.location.href = `/narrative-explorer?narrative=${narrativeId}`;
  };

  useEffect(() => {
    // Set page title
    document.title = `Claim Analysis: ${claimData?.statement?.substring(0, 50)}... - TruthGuard EU`;
  }, [claimData?.statement]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`
        transition-all duration-300 ease-in-out pt-16
        ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
      `}>
        <div className="container mx-auto px-6 py-8">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
            <a href="/public-claim-search" className="hover:text-foreground transition-smooth">
              Claim Search
            </a>
            <span>•</span>
            <span className="text-foreground">Analysis Details</span>
          </nav>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Primary Content */}
            <div className="xl:col-span-2 space-y-8">
              {/* Claim Header */}
              <ClaimHeader
                claim={claimData}
                onShare={handleShare}
                onExport={handleExport}
                onRequestReview={handleRequestReview}
              />

              {/* View Toggle */}
              <div className="flex items-center space-x-1 bg-muted/30 rounded-lg p-1">
                <button
                  onClick={() => setActiveView('overview')}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                    ${activeView === 'overview' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveView('evidence')}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                    ${activeView === 'evidence' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  Evidence
                </button>
                <button
                  onClick={() => setActiveView('timeline')}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                    ${activeView === 'timeline' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  Timeline
                </button>
              </div>

              {/* Content Based on Active View */}
              {activeView === 'overview' && (
                <div className="space-y-8">
                  <EvidenceSection evidence={evidenceData} />
                  <VerdictTimeline 
                    timeline={timelineData?.slice(0, 2)} 
                    onViewDiff={handleViewDiff} 
                  />
                </div>
              )}

              {activeView === 'evidence' && (
                <EvidenceSection evidence={evidenceData} />
              )}

              {activeView === 'timeline' && (
                <VerdictTimeline 
                  timeline={timelineData} 
                  onViewDiff={handleViewDiff} 
                />
              )}
            </div>

            {/* Secondary Panel */}
            <div className="xl:col-span-1">
              <div className="sticky top-24 space-y-6">
                <RelatedClaimsPanel
                  relatedClaims={relatedClaimsData}
                  narrativeConnections={narrativeConnectionsData}
                  onClaimClick={handleClaimClick}
                  onNarrativeClick={handleNarrativeClick}
                />

                {/* Quick Actions */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-soft">
                  <h4 className="font-medium text-foreground mb-3">Quick Actions</h4>
                  <div className="space-y-2">
                    <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-smooth">
                      <span>🔖</span>
                      <span>Bookmark Analysis</span>
                    </button>
                    <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-smooth">
                      <span>📧</span>
                      <span>Email Report</span>
                    </button>
                    <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-smooth">
                      <span>🚨</span>
                      <span>Report Issue</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Share/Export Modal */}
      <ShareExportModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        claim={claimData}
      />
    </div>
  );
};

export default ClaimDetailAnalysis;